import styled from '@emotion/styled';
import { List, ListItem as _ListItem, Table, TableBody, TableCell, TableRow, Typography } from '@material-ui/core';
import { createElement, FC } from 'react';
import { FormattedMessage } from 'react-intl';

import ProductPricing from '../../../../entity/ProductPricing';
import theme from '../../../../theme';
import { calculatePoint } from '../../../../utility/calculatePoint';
import { useGetIsPC } from '../../../../utilityHooks/useGetIsPC';
import { SPACING_REGULAR } from '../../../constant/spacing';
import { flexibleLayoutContainer, flexibleLayoutItem } from '../../../styleUtility/flexibleLayout';
import { InputtedCreationSet } from '../../../types/InputtedCreationSet';
import { InputtedCreativeFormat } from '../../../types/InputtedCreativeFormat';
import CreativeFormatCreativeTypeIcon from '../CreativeFormatCreativeTypeIcon';
import _NetworkIcon from '../CreativeFormatNetworkIcon';
import CreativeFormatPlacementLabel from '../CreativeFormatPlacementLabel';

type Props = {
  creationSet: InputtedCreationSet;
  isPointRequired: boolean;
  productPricing?: ProductPricing;
  className?: string;
};

const CreationSetList: FC<Props> = ({ creationSet, isPointRequired, productPricing, className }) => {
  const { creativeFormats } = creationSet;
  const emptyCell = <DenserTableCell>-</DenserTableCell>;
  const sortedCreativeFormats: InputtedCreativeFormat[] = [];
  const resizeCreativeFormats = creativeFormats.filter((creativeFormat) => !!creativeFormat.resizeBaseId);

  creativeFormats.map((creativeFormat) => {
    if (!creativeFormat.resizeBaseId) {
      sortedCreativeFormats.push(creativeFormat);

      resizeCreativeFormats.map((resizeCreativeFormat) => {
        if (resizeCreativeFormat.resizeBaseId === creativeFormat.id) {
          sortedCreativeFormats.push(resizeCreativeFormat);
        }
      });
    }
  });
  const isPC = useGetIsPC();

  return (
    <Root>
      <CreationSetListItem className={className}>
        <Body>
          {isPC ? (
            <Table>
              <TableBody>
                {sortedCreativeFormats.map((creativeFormat) => {
                  const { network, placement, attribute, creativeType } = creativeFormat;
                  const requiredPoint = calculatePoint(creativeFormat);

                  const creativeTypeCell = (
                    <DenserTableCell>
                      {!creativeFormat.resizeBaseId ? (
                        <CreativeFormatCreativeTypeIcon creativeType={creativeType} />
                      ) : null}
                    </DenserTableCell>
                  );

                  const networkCell = (
                    <DenserTableCell>
                      <NetworkIcon network={network} />{' '}
                    </DenserTableCell>
                  );

                  const placementCell = (
                    <DenserTableCell>
                      <Placement>
                        <CreativeFormatPlacementLabel network={network} placement={placement} />
                      </Placement>
                    </DenserTableCell>
                  );

                  const pointCell =
                    isPointRequired && productPricing ? (
                      <DenserTableCell>
                        <AlignRight>
                          <RequiredPoint>
                            {productPricing.currency}
                            {(requiredPoint * productPricing.flatRate).toLocaleString()}
                          </RequiredPoint>
                        </AlignRight>
                      </DenserTableCell>
                    ) : null;

                  const TableBodyRow = creativeFormat.resizeBaseId ? TableRow : TableRowBg;

                  if (creativeType.key === 'video') {
                    return (
                      <TableBodyRow key={`creation_set_table_row_${creativeFormat.id}`}>
                        {creativeTypeCell}
                        {networkCell}
                        {placementCell}
                        <DenserTableCell>
                          <Size>
                            {attribute.aspectRatio &&
                            attribute.aspectRatio.aspectRatio.width * attribute.aspectRatio.aspectRatio.height > 0 ? (
                              `${attribute.aspectRatio.aspectRatio.width}:${attribute.aspectRatio.aspectRatio.height} (${attribute.aspectRatio.size.width}x${attribute.aspectRatio.size.height})`
                            ) : (
                              <FormattedMessage id="creative_format.aspect_ratio.nullable" />
                            )}
                          </Size>
                        </DenserTableCell>
                        <DenserTableCell>
                          <Length>
                            {attribute.playbackTime ? (
                              <FormattedMessage
                                id="creative_format.playback_time"
                                values={{ seconds: attribute.playbackTime.seconds }}
                              />
                            ) : (
                              '-'
                            )}
                          </Length>
                        </DenserTableCell>
                        {pointCell}
                      </TableBodyRow>
                    );
                  } else if (creativeType.key === 'static_image') {
                    return (
                      <TableBodyRow key={creativeFormat.id}>
                        {creativeTypeCell}
                        {networkCell}
                        {placementCell}
                        <DenserTableCell>
                          <Size>
                            {attribute.aspectRatio &&
                            attribute.aspectRatio.aspectRatio.width * attribute.aspectRatio.aspectRatio.height > 0 ? (
                              `${attribute.aspectRatio.aspectRatio.width}:${attribute.aspectRatio.aspectRatio.height} (${attribute.aspectRatio.size.width}x${attribute.aspectRatio.size.height})`
                            ) : (
                              <FormattedMessage id="creative_format.aspect_ratio.nullable" />
                            )}
                          </Size>
                        </DenserTableCell>
                        {emptyCell}
                        {pointCell}
                      </TableBodyRow>
                    );
                  } else {
                    return (
                      <TableBodyRow key={creativeFormat.id}>
                        {creativeTypeCell}
                        {networkCell}
                        {placementCell}
                        {emptyCell}
                        {emptyCell}
                        {emptyCell}
                        {pointCell}
                      </TableBodyRow>
                    );
                  }
                })}
              </TableBody>
            </Table>
          ) : (
            <List>
              {sortedCreativeFormats.map((creativeFormat) => {
                const { network, placement, attribute, creativeType } = creativeFormat;
                const requiredPoint = calculatePoint(creativeFormat);

                const creativeTypeCell = (
                  <DenserTableCellCreativeIcon>
                    {!creativeFormat.resizeBaseId ? (
                      <CreativeFormatCreativeTypeIcon creativeType={creativeType} />
                    ) : null}
                  </DenserTableCellCreativeIcon>
                );

                const networkCell = (
                  <DenserTableCell>
                    <NetworkIcon network={network} />{' '}
                  </DenserTableCell>
                );

                const placementCell = (
                  <DenserTableCell>
                    <Placement>
                      <CreativeFormatPlacementLabel network={network} placement={placement} />
                    </Placement>
                  </DenserTableCell>
                );

                const pointCell =
                  isPointRequired && productPricing ? (
                    <DenserTableCell>
                      <AlignRight>
                        <RequiredPoint>
                          {productPricing.currency}
                          {(requiredPoint * productPricing.flatRate).toLocaleString()}
                        </RequiredPoint>
                      </AlignRight>
                    </DenserTableCell>
                  ) : null;

                const ListRow = creativeFormat.resizeBaseId ? ListItem : ListItemBg;

                if (creativeType.key === 'video') {
                  return (
                    <ListRow button key={`creation_set_table_row_${creativeFormat.id}`}>
                      <TableRowItem>
                        {creativeTypeCell}
                        {networkCell}
                        {placementCell}
                      </TableRowItem>
                      <TableRowItem>
                        <DenserTableCell>
                          <Size>
                            {attribute.aspectRatio &&
                            attribute.aspectRatio.aspectRatio.width * attribute.aspectRatio.aspectRatio.height > 0 ? (
                              `${attribute.aspectRatio.aspectRatio.width}:${attribute.aspectRatio.aspectRatio.height} (${attribute.aspectRatio.size.width}x${attribute.aspectRatio.size.height})`
                            ) : (
                              <FormattedMessage id="creative_format.aspect_ratio.nullable" />
                            )}
                          </Size>
                        </DenserTableCell>
                        <DenserTableCell>
                          <Length>
                            {attribute.playbackTime ? (
                              <FormattedMessage
                                id="creative_format.playback_time"
                                values={{ seconds: attribute.playbackTime.seconds }}
                              />
                            ) : (
                              '-'
                            )}
                          </Length>
                        </DenserTableCell>
                        {pointCell}
                      </TableRowItem>
                    </ListRow>
                  );
                } else if (creativeType.key === 'static_image') {
                  return (
                    <ListRow button key={creativeFormat.id}>
                      <TableRowItem>
                        {creativeTypeCell}
                        {networkCell}
                        {placementCell}
                      </TableRowItem>
                      <TableRowItem>
                        <DenserTableCell>
                          <Size>
                            {attribute.aspectRatio &&
                            attribute.aspectRatio.aspectRatio.width * attribute.aspectRatio.aspectRatio.height > 0 ? (
                              `${attribute.aspectRatio.aspectRatio.width}:${attribute.aspectRatio.aspectRatio.height} (${attribute.aspectRatio.size.width}x${attribute.aspectRatio.size.height})`
                            ) : (
                              <FormattedMessage id="creative_format.aspect_ratio.nullable" />
                            )}
                          </Size>
                        </DenserTableCell>
                        {pointCell}
                      </TableRowItem>
                    </ListRow>
                  );
                } else {
                  return (
                    <ListRow button key={creativeFormat.id}>
                      <TableRowItem>
                        {creativeTypeCell}
                        {networkCell}
                        {placementCell}
                        {pointCell}
                      </TableRowItem>
                    </ListRow>
                  );
                }
              })}
            </List>
          )}
        </Body>
      </CreationSetListItem>
    </Root>
  );
};

const Root = styled('div')`
  ${flexibleLayoutContainer({
    direction: 'column',
  })}
`;

const TableRowBg = styled(TableRow)`
  background-color: #f2f6fa;
`;

const CreationSetListItem = styled('div')`
  ${flexibleLayoutItem({})}
  margin-bottom: ${SPACING_REGULAR}px;
`;

const Body = styled('div')`
  ${theme.breakpoints.down('md')} {
    overflow: auto;
  }
`;

const AlignRight = styled('div')`
  text-align: right;
`;

const NetworkIcon = styled(_NetworkIcon)`
  height: 24px;
  width: 24px;
`;

const DenserTableCell = styled(TableCell)`
  ${theme.breakpoints.down('md')} {
    padding: 16px 10px;
  }
  ${theme.breakpoints.down('sm')} {
    padding: 0 5px;
    border-bottom: none;
  }
`;
DenserTableCell.defaultProps = { size: 'medium' };

const ColumnHeader = styled(Typography)``;
ColumnHeader.defaultProps = {
  variant: 'caption',
  color: 'textSecondary',
  noWrap: true,
};

const Placement = styled(Typography)`
  width: 140px;
  word-break: break-word;
`;

const Size = styled(Typography)``;
const Length = styled(Typography)``;
Length.defaultProps = { noWrap: true };

const RequiredPoint = styled(Typography)``;
RequiredPoint.defaultProps = { variant: 'body1' };

const ListItem = styled(_ListItem)`
  flex-direction: column;
  align-items: stretch;
  border-bottom: 1px solid rgba(224, 224, 224, 1);
`;

const ListItemBg = styled(ListItem)`
  background-color: #f2f6fa;
`;

const TableRowItem = styled(TableRow)`
  &:last-of-type {
    display: flex;
    justify-content: space-between;
  }
`;

const DenserTableCellCreativeIcon = styled(DenserTableCell)`
  min-width: 35px;
  box-sizing: border-box;
`;

export default CreationSetList;
